import { useBankAccounts } from '@/hooks/queries/useBankIngestion';
import { usePaymentProcessors } from '@/hooks/queries/usePaymentProcessorRate';
import { useFinanceSettings } from '@/hooks/queries/useFinanceSettings';
import { useCurrentClosePeriod } from '@/hooks/queries/useCloseHub';

/**
 * CPH-012 onboarding precedence: drives the Close hub's pre-spine flow.
 *
 * USI-005 (ADR-011) collapsed the three-phase tracker; the
 * `spine:waiting-for-activity` state retired with `usePhaseState` (no
 * `phase.sort.tied` to delegate to). The remaining four states keep their
 * meaning:
 *
 * - `loading` — the gate-deciding queries are still in flight on a cold load;
 *   we don't yet know if this is a fresh campaign or an established one, so the
 *   layout holds a neutral skeleton instead of guessing a gate.
 * - `gate:no-sources` — link a bank or processor first.
 * - `gate:no-formation-date` — set the committee formation date first.
 * - `no-period` — no open `FilingDeadline` resolves; show the empty state.
 * - `spine:active` — render the inbox (post-USI-007) inside the layout.
 */
export type CloseHubOnboardingState =
  | 'loading'
  | 'gate:no-sources'
  | 'gate:no-formation-date'
  | 'no-period'
  | 'spine:active';

export function useCloseHubOnboardingState(): CloseHubOnboardingState {
  const bankAccounts = useBankAccounts();
  const processors = usePaymentProcessors();
  const settings = useFinanceSettings();
  const period = useCurrentClosePeriod();

  // Loading-state policy (coordinated with the `CloseLayout` consumer): while
  // ANY gate-deciding query is still in flight on a cold load, we cannot tell a
  // freshly-onboarded campaign (genuinely no sources) from an established one
  // whose data simply hasn't arrived. Reporting a gate here flashed "Connect a
  // source to start." over real history and read as data loss. Report `loading`
  // instead and let the layout hold a neutral skeleton until the truth arrives.
  // TanStack v5 `isLoading` is `isPending && isFetching`, so it is true only on
  // the FIRST fetch with no cache — background refetches won't re-trigger this,
  // and disabled queries (no campaign selected) report false.
  if (
    bankAccounts.isLoading ||
    processors.isLoading ||
    settings.isLoading ||
    period.isLoading
  ) {
    return 'loading';
  }

  const hasBank = (bankAccounts.data?.length ?? 0) > 0;
  const hasProcessor = (processors.data?.length ?? 0) > 0;

  // A mid-cycle onboarded campaign (ADR-018) has real filed history — frozen externally-filed
  // reports + Reported ledger rows — before any live source is connected. Its
  // PriorFilingThroughDate is the backfill marker; when set, skip the "connect a source" gate so
  // the register shows that locked history (under the "Older than this period" divider) instead of
  // a blank empty state that reads as "nothing imported." Connecting a bank/processor stays the
  // obvious next step for the open period — it's just no longer a hard gate over real history.
  const hasPriorFilingHistory = settings.data?.priorFilingThroughDate != null;
  if (!hasBank && !hasProcessor && !hasPriorFilingHistory) return 'gate:no-sources';

  const formationDate = settings.data?.committeeFormationDate ?? null;
  if (!formationDate) return 'gate:no-formation-date';

  if (period.data === null) return 'no-period';
  return 'spine:active';
}
